/* eslint-disable camelcase */

import React, {
  useState, useRef, useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Form, OverlayView } from '@piwikpro/ui-components';
import 'moment-timezone';
import { FormikActions, FormikProps } from 'formik';
import TitleComponent from '@piwikpro/title-crate/Title';
import currencies from '@piwikpro/platform/config/currencies.json';
import {
  withInject, errorBoundary, ConfigService, view,
} from '@piwikpro/platform';
import {
  App, Apps, mapConsentManagementToolToApiAttributes, resolveConsentManagementTool,
} from '@piwikpro/apps-crate';
import { FormValidator } from '@piwikpro/form-crate';
import { UnsavedChangesTracker } from '@piwikpro/form-crate/components/UnsavedChangesTracker';
import { SessionState } from '@piwikpro/auth-crate/reducers/session';
import { TrackerSettings } from '@piwikpro/tracker-settings-crate';
import { useQuery } from '@piwikpro/url';
import { TrackerSettingsState } from '@piwikpro/tracker-settings-crate/redux/app';
import { RouterService } from '@piwikpro/router-crate';
import { Notifications } from '@piwikpro/notification-crate';
import { connect, ConnectedProps } from 'react-redux';
import { CookieInformationState } from '@piwikpro/cookie-information-crate/redux';
import { OrganizationState } from '@piwikpro/organization-crate';
import FormOverlay from './FormOverlay/FormOverlay';
import SuccessView from './SuccessView/SuccessView';
import BasicConfigurationStep from './BasicConfigurationStep';
import ConsentManagerStep from './ConsentManagerStep';
import { AppType, ConsentManagementTool } from '../../model';
import Installation from './InstallationStep';
import type { FormValues } from './types';
import { MOBILE_APP_STEPS, WEBSITE_APP_STEPS } from './steps';
import { FullscreenOverlay } from '../FullscreenOverlay/FullscreenOverlay';

const APP_TYPE_DEFAULT: AppType = 'website';
const CONSENT_OPTION_DEFAULT: ConsentManagementTool = 'CookieInformationCMP';

const getInstallationStep = (isWebsiteApp: boolean): number => (
  isWebsiteApp ? WEBSITE_APP_STEPS.INSTALLATION : MOBILE_APP_STEPS.INSTALLATION
);

const isPastLastStep = (step: number, isWebsiteApp: boolean): boolean => (
  step > getInstallationStep(isWebsiteApp)
);

interface InjectedProps {
  apps: Apps
  trackerSettings: TrackerSettings
  config: ConfigService
  validation: FormValidator
  Title: typeof TitleComponent
  routerService: RouterService
  notifications: Notifications
}

const mapStateToProps = (state: CookieInformationState & { organization: OrganizationState }) => {
  const { status } = state.cookieInformation.account;

  return {
    isCiAccountReady: status === 'showingCreateAccountSuccess' || status === 'showingLinkedAccount',
    isPiwikProConsentManagerIncludedInLicense: Boolean(state.organization.details.license?.current?.modules?.includes('consent_manager')),
  };
};

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface Props extends InjectedProps, ReduxProps {
  filters: any
  existingApp: App | null
  loggedUserEmail: SessionState['email']
  appTrackerSettings: TrackerSettingsState['settings']
  globalTrackerSettings: TrackerSettingsState['settings']
}

const parseAnalyticsCurrencies = (currenciesList: string[][]) => currenciesList.map(currency => ({
  id: currency[0],
  value: currency[0],
  name: `${currency[1]} (${currency[0]})`,
}));

const getAppConsentManagementTool = (app: App | null): ConsentManagementTool | null => {
  if (!app) {
    return CONSENT_OPTION_DEFAULT;
  }

  return resolveConsentManagementTool(app.attributes);
};

export const AppCreator = ({
  Title,
  validation,
  config,
  apps,
  filters,
  existingApp,
  loggedUserEmail,
  trackerSettings,
  appTrackerSettings,
  globalTrackerSettings,
  isPiwikProConsentManagerIncludedInLicense,
  notifications,
  isCiAccountReady,
  routerService,
}: Props) => {
  const { t } = useTranslation(['administration', 'platform']);

  const prefilledParam = useQuery('prefilled');
  const prefilled = prefilledParam === 'true';
  const consentManagementTool = getAppConsentManagementTool(prefilled ? existingApp : null);
  const consentSourceKey = prefilled ? (existingApp?.id ?? 'prefilled-loading') : 'new';
  const [initialConsentOption, setInitialConsentOption] = useState(consentManagementTool);
  const [consentOption, setConsentOption] = useState(consentManagementTool);
  const [createdApp, setCreatedApp] = useState<App | null>(null);
  const [isCreatingApp, setIsCreatingApp] = useState(false);
  const typeParam: AppType | null = useQuery<AppType>('type');
  const [timezoneSearch, setTimezoneSearch] = useState('');
  const [currencySearch, setCurrencySearch] = useState('');
  const [selectedOption, setSelectedOption] = useState<AppType>(typeParam || APP_TYPE_DEFAULT);
  const [cookieFormDirty, setCookieFormDirty] = useState(false);
  const isConsentManagerStepReady = consentOption !== 'CookieInformationCMP' || isCiAccountReady;
  const [progressStepperStep, setProgressStepperStep] = useState(0);
  const analyticsCurrencies = parseAnalyticsCurrencies(currencies);
  const isWebsiteApp = selectedOption === 'website';
  const consentSyncRef = useRef<{ sourceKey: string, consentOptionFromApp: ConsentManagementTool | null } | null>(null);
  const pendingStepChangeRef = useRef<number | null>(null);
  const formikApiRef = useRef<FormikProps<FormValues> | null>(null);

  const isOnInstallationStep = progressStepperStep === getInstallationStep(isWebsiteApp);
  const shouldBeSubmitted = isWebsiteApp
    ? progressStepperStep === WEBSITE_APP_STEPS.CONSENT_MANAGER
    : progressStepperStep === MOBILE_APP_STEPS.BASIC_CONFIGURATION;

  const getPrimaryButtonTextKey = () => {
    if (isOnInstallationStep) return 'apps.creator.finishButton';
    if (shouldBeSubmitted) return 'apps.creator.saveButton';
    return 'apps.creator.nextButton';
  };

  const handleProgressStepperStepChange = async (step: number, handleSubmit?: () => void) => {
    if (shouldBeSubmitted) {
      await handleSubmit?.();
    }

    const isInstallationStep = step === getInstallationStep(isWebsiteApp);
    if (isInstallationStep && !createdApp) {
      pendingStepChangeRef.current = step;
      return;
    }

    setProgressStepperStep(step);
    pendingStepChangeRef.current = null;
  };

  useEffect(() => {
    if (createdApp && pendingStepChangeRef.current !== null && !isCreatingApp) {
      setProgressStepperStep(pendingStepChangeRef.current);
      pendingStepChangeRef.current = null;
    }
  }, [createdApp, isCreatingApp]);

  useEffect(() => {
    const lastSynced = consentSyncRef.current;
    const sourceChanged = lastSynced?.sourceKey !== consentSourceKey;
    const appConsentChanged = lastSynced?.consentOptionFromApp !== consentManagementTool;

    if (!sourceChanged && !appConsentChanged) {
      return;
    }

    if (!sourceChanged && appConsentChanged && consentOption !== initialConsentOption) {
      consentSyncRef.current = { sourceKey: consentSourceKey, consentOptionFromApp: consentManagementTool };
      return;
    }

    setInitialConsentOption(consentManagementTool);
    setConsentOption(consentManagementTool);
    setCookieFormDirty(false);
    consentSyncRef.current = { sourceKey: consentSourceKey, consentOptionFromApp: consentManagementTool };
  }, [consentManagementTool, consentSourceKey, consentOption, initialConsentOption]);

  const handleSegmentedSelectionChange = (value: 'website' | 'mobile-app') => {
    setSelectedOption(value);
  };

  const handleCancel = () => {
    if (prefilled) {
      routerService.back();
    } else {
      routerService.dismissAdministrationOverlay();
    }
  };

  const handleSubmit = async (
    values: FormValues,
    { setErrors, setSubmitting }: FormikActions<FormValues>,
  ) => {
    let trackerSettingsUpdateFailed = false;

    try {
      let app: App;
      const appAttributes = {
        name: values.name,
        urls: values.urls,
        gdprDataAnonymization: values.gdprDataAnonymization,
        gdprDataAnonymizationMode: values.gdprDataAnonymizationMode,
        privacyUseCookies: values.privacyUseCookies,
        timezone: values.timezone,
        currency: values.currency,
        realTimeDashboards: values.realTimeDashboards,
        eCommerceTracking: values.eCommerceTracking,
        ...isWebsiteApp ? mapConsentManagementToolToApiAttributes(consentOption) : {},
      };
      setIsCreatingApp(true);
      const currentApp = prefilled ? existingApp : createdApp;
      if (currentApp) {
        await apps.update(currentApp.id, appAttributes, { withConfirmation: false }, true);
        app = {
          ...currentApp,
          attributes: {
            ...currentApp.attributes,
            ...appAttributes,
          },
        };
      } else {
        app = await apps.create(appAttributes, { hideConfirmation: true });
      }

      try {
        await trackerSettings.updateSettingsForApp(app.id, {
          urls: values.urls,
          use_anonymous_session_hash: values.use_anonymous_session_hash,
          use_session_hash: values.use_session_hash,
        }, { hideConfirmation: true });
      } catch (e) {
        trackerSettingsUpdateFailed = true;
        throw e;
      }

      if (currentApp) {
        await trackerSettings.getDetailsForApp(currentApp.id);
      }

      await apps.find({ offset: 0, ...filters });
      await apps.get(app.id);

      setCreatedApp(app);
      setInitialConsentOption(consentOption);
      setCookieFormDirty(false);
    } catch (err: any) {
      if (!trackerSettingsUpdateFailed) {
        setProgressStepperStep(isWebsiteApp ? WEBSITE_APP_STEPS.BASIC_CONFIGURATION : MOBILE_APP_STEPS.BASIC_CONFIGURATION);
      }

      const apiErrors = err?.errors || {};
      const formikErrors: any = { urls: [], name: '' };

      if (apiErrors.name) {
        formikErrors.name = t('apps.creator.validation.invalidName');
      }

      Object.keys(apiErrors).forEach((key) => {
        if (Number.isInteger(Number(key))) {
          const index = Number(key);
          formikErrors.urls[index] = t('apps.creator.validation.invalidUrl');
        }
      });

      formikErrors.urls = formikErrors.urls.map(v => v ?? '');

      setErrors(formikErrors);

      const hasFieldErrors = Boolean(apiErrors.name)
        || Object.keys(apiErrors).some(key => Number.isInteger(Number(key)));

      if (!hasFieldErrors) {
        notifications.push({
          text: err?.name === 'InvalidRequestError'
            ? t('components:request.invalid.explanation')
            : t('components:request.failed.explanation'),
          type: 'error',
          autoCloseSeconds: 5,
        });
      }
    } finally {
      setSubmitting(false);
      setIsCreatingApp(false);
    }
  };

  if (isPastLastStep(progressStepperStep, isWebsiteApp) && createdApp) {
    if (prefilled) {
      return (
        <FullscreenOverlay>
          <SuccessView app={createdApp} isWebsiteApp={isWebsiteApp} showLogo />
        </FullscreenOverlay>
      );
    }

    return (
      <OverlayView onClose={handleCancel} appearAnimation={false} isOpen title={t('apps.creator.title')}>
        <SuccessView app={createdApp} isWebsiteApp={isWebsiteApp} showLogo={false} />
      </OverlayView>
    );
  }

  let initialUseAnonymousSessionHash = true;
  let initialUseSessionHash = true;

  if (prefilled) {
    const globalUseAnonymousSessionHash = globalTrackerSettings?.attributes?.use_anonymous_session_hash;
    const globalUseSessionHash = globalTrackerSettings?.attributes?.use_session_hash;
    const appUseAnonymousSessionHash = appTrackerSettings?.attributes?.use_anonymous_session_hash;
    const appUseSessionHash = appTrackerSettings?.attributes?.use_session_hash;
    initialUseAnonymousSessionHash = appUseAnonymousSessionHash === undefined ? globalUseAnonymousSessionHash : appUseAnonymousSessionHash;
    initialUseSessionHash = appUseSessionHash === undefined ? globalUseSessionHash : appUseSessionHash;
  }

  const handleClose = () => {
    formikApiRef.current?.resetForm();

    handleCancel();
  };

  return (
    <Title part={t(prefilled ? 'app.creator.documentTitle.editApp' : 'app.creator.documentTitle.addApp')} subset>
      <Form
        id="apps-create-form"
        isInitialValid={prefilled}
        initialValues={{
          name: prefilled && existingApp ? existingApp.attributes.name : '',
          urls: prefilled && existingApp ? existingApp.attributes.urls : [],
          timezone: prefilled && existingApp ? existingApp.attributes.timezone : 'UTC',
          currency: prefilled && existingApp ? existingApp.attributes.currency : 'USD',
          realTimeDashboards: prefilled && existingApp ? existingApp.attributes.realTimeDashboards : true,
          eCommerceTracking: prefilled && existingApp ? existingApp.attributes.eCommerceTracking : true,
          gdprDataAnonymization: prefilled && existingApp ? existingApp.attributes.gdprDataAnonymization : true,
          gdprDataAnonymizationMode: prefilled && existingApp ? existingApp.attributes.gdprDataAnonymizationMode : 'session_cookie_id',
          privacyUseCookies: prefilled && existingApp ? existingApp.attributes.privacyUseCookies : true,
          use_anonymous_session_hash: initialUseAnonymousSessionHash,
          use_session_hash: initialUseSessionHash,
        }}
        validationSchema={validation.createValidationSchema(validators => ({
          name: validators.apps.name,
          // @ts-ignore
          urls: validators.apps.urls,
          timezone: validators.apps.timezone,
          currency: validators.apps.currency,
        }))}
        onSubmit={handleSubmit}
        render={(api: FormikProps<FormValues>) => {
          formikApiRef.current = api;

          const isAppTypeModified = prefilled ? selectedOption !== typeParam : selectedOption !== APP_TYPE_DEFAULT;
          const isConsentOptionModified = consentOption !== initialConsentOption;
          const isCreatorModified = api.dirty
            || isAppTypeModified
            || isConsentOptionModified
            || cookieFormDirty;

          return (
            <UnsavedChangesTracker
              type="twoOptions"
              discardAction={handleClose}
              isModified={isCreatorModified}
              render={({ UnsavedChangesModal, onClose }) => (
                <FormOverlay
                  api={api}
                  progressStepperStep={progressStepperStep}
                  isWebsiteApp={isWebsiteApp}
                  prefilled={prefilled}
                  isCreatingApp={isCreatingApp}
                  isConsentManagerStepReady={isConsentManagerStepReady}
                  handleProgressStepperStepChange={handleProgressStepperStepChange}
                  getPrimaryButtonTextKey={getPrimaryButtonTextKey}
                  UnsavedChangesModal={UnsavedChangesModal}
                  onClose={onClose}
                >
                  {progressStepperStep === WEBSITE_APP_STEPS.BASIC_CONFIGURATION && (
                    <BasicConfigurationStep
                      t={t}
                      selectedOption={selectedOption}
                      onChangeSelectedOption={handleSegmentedSelectionChange}
                      timezones={config.get('AVAILABLE_TIME_ZONES')}
                      currencies={analyticsCurrencies}
                      timezoneSearch={timezoneSearch}
                      onTimezoneSearch={val => setTimezoneSearch(val)}
                      onTimezoneOpen={() => setTimezoneSearch('')}
                      currencySearch={currencySearch}
                      onCurrencySearch={val => setCurrencySearch(val)}
                      onCurrencyOpen={() => setCurrencySearch('')}
                      previewTimezone={createdApp?.attributes.timezone || 'UTC'}
                    />
                  )}
                  {progressStepperStep === WEBSITE_APP_STEPS.CONSENT_MANAGER && isWebsiteApp && (
                    <ConsentManagerStep
                      consentOption={consentOption}
                      onChangeConsentOption={setConsentOption}
                      isPiwikProConsentManagerIncludedInLicense={isPiwikProConsentManagerIncludedInLicense}
                      loggedUserEmail={loggedUserEmail}
                      setCookieFormDirty={setCookieFormDirty}
                      api={api}
                    />
                  )}
                  {progressStepperStep === getInstallationStep(isWebsiteApp) && (
                    <Installation
                      isCreatingApp={isCreatingApp}
                      app={createdApp as App}
                      appType={selectedOption}
                      cmp={consentOption}
                    />
                  )}
                </FormOverlay>
              )}
            />
          );
        }}
      />
    </Title>
  );
};

export default connector(withInject<InjectedProps>({
  Title: 'TitleCrate.components.Title',
  apps: 'AppsCrate.apps',
  trackerSettings: 'TrackerSettingsCrate.tracker',
  config: 'config',
  validation: 'FormCrate.validation',
  routerService: 'RouterCrate.router',
  notifications: 'NotificationCrate.notifications',
})(view<Props, InjectedProps>({ role: 'OWNER' })(errorBoundary(AppCreator))));
